import {defineStore} from 'pinia';
import ApiService from '@core/services/ApiService';
import {SavedOptions} from '@core/helpers/persistOptionsHelper';
import {PermissionGroup} from '@core/store/AdminStore';
import {AxiosResponse} from 'axios';
import {ref} from 'vue';
import {ApiBackendTeam} from './Environment-ApiProductStore';

interface SharedApplication {
  id: string;
  appId: string;
  teamId: string;
  sharedBy?: string;
  sharedByYou?: boolean;
}

export interface Member {
  id: string;
  developerId: string;
  teamId: string;
  role: string;
  email: string;
  isYou?: boolean;
}

export interface Team {
  id: string;
  name: string;
  createdAt?: string;
  updatedAt?: string;
  role?: string;
  teamType: 'normal' | 'backend';
  members: Member[];
  sharedApplications?: SharedApplication[];
  permissionGroups: PermissionGroup[];
  apis?: ApiBackendTeam[];
}

interface NewTeam {
  name: string;
  teamType: 'normal' | 'backend';
}

export const useTeamStore = defineStore(
  'TeamStore',
  () => {
    const api = ApiService.getApiInstance();

    const teams = ref<Team[]>([]);
    const createdTeamId = ref<string | null>(null);
    const hasTeam = ref<null | boolean>(null);
    const newOptions = ref<SavedOptions>({
      page: 1,
      itemsPerPage: 10,
      sortBy: [],
      groupBy: [],
      multiSort: false,
      mustSort: false,
      filter: {},
    });
    const permissionGroupOptions = ref<SavedOptions>({
      page: 1,
      itemsPerPage: 10,
      sortBy: [],
      groupBy: [],
      multiSort: false,
      mustSort: false,
      filter: {},
    });
    const memberOptions = ref<SavedOptions>({
      page: 1,
      itemsPerPage: 10,
      sortBy: [],
      groupBy: [],
      multiSort: false,
      mustSort: false,
      filter: {},
    });
    const permissionGroupTOptions = ref<SavedOptions>({
      page: 1,
      itemsPerPage: 10,
      sortBy: [],
      groupBy: [],
      multiSort: false,
      mustSort: false,
      filter: {},
    });
    const apisOptions = ref<SavedOptions>({
      page: 1,
      itemsPerPage: 10,
      sortBy: [],
      groupBy: [],
      multiSort: false,
      mustSort: false,
      filter: {},
    });
    const newSearch = ref<string>('');
    const currentTeam = ref<Team>({} as Team);
    const loadingTeam = ref(false);

    function getSharedTeams(id: string, teams: Team[]) {
      return teams
        .filter(team =>
          (team.sharedApplications ?? []).find(app => app.appId === id),
        )
        .map(team => ({
          name: team.name,
          id: team.id,
        }));
    }

    function getNotSharedTeams(id: string, teams: Team[]) {
      const filteredTeams = teams.filter(
        team =>
          !team.sharedApplications ||
          team.sharedApplications.length === 0 ||
          !team.sharedApplications.some(app => app.appId === id),
      );

      return filteredTeams.map(team => ({
        name: team.name,
        id: team.id,
      }));
    }

    function getTeamId(teams: Team[], teamId: string) {
      return teams.some(team => team.id === teamId) ? teamId : undefined;
    }

    function getTeamById(id: string): Team | undefined {
      return teams.value.find(team => team.id === id);
    }

    function getTeamMembersTable(id: string) {
      const teamFound = teams.value.find(team => team.id === id);
      if (teamFound) {
        return teamFound.members;
      }
      return [];
    }

    function applyTeamWithoutMember(teamId: string, memberId: string) {
      teams.value = teams.value.map(team =>
        team.id === teamId
          ? {
              ...team,
              members: (team.members ?? []).filter(
                member => member.id !== memberId,
              ),
            }
          : team,
      );
      if (currentTeam.value?.id === teamId) {
        currentTeam.value = {
          ...currentTeam.value,
          members: (currentTeam.value.members ?? []).filter(
            member => member.id !== memberId,
          ),
        };
      }
    }

    async function createTeam(team: NewTeam): Promise<AxiosResponse> {
      const resp = await api.post('/teams', team);
      createdTeamId.value = resp.data.id;
      return resp;
    }

    async function loadTeams() {
      return api
        .get(
          '/teams?filter={"include":["members", "sharedApplications", "permissionGroups", "apis"]}',
        )
        .then((response: AxiosResponse) => {
          teams.value = response.data;
          hasTeam.value = teams.value.length > 0;
          if (teams.value.length > 0) {
            const currentId = currentTeam.value?.id;
            const match = currentId
              ? teams.value.find(t => t.id === currentId)
              : undefined;
            currentTeam.value =
              match ??
              (Object.keys(currentTeam.value).length === 0
                ? teams.value[0]
                : currentTeam.value);
          } else {
            currentTeam.value = {} as Team;
          }
        });
    }

    async function loadTeamById(id: string | string[]) {
      const teamId = Array.isArray(id) ? id[0] : id;
      if (!teamId) {
        return;
      }
      loadingTeam.value = true;
      try {
        const resp = await api.get(
          `/teams/${teamId}?filter={"include":["members", "sharedApplications", "permissionGroups", "apis"]}`,
        );
        currentTeam.value = resp.data;
        const teamIndex = teams.value.findIndex(team => team.id === teamId);
        if (teamIndex === -1) {
          teams.value.push(resp.data);
        } else {
          teams.value[teamIndex] = resp.data;
        }
        return resp.data;
      } finally {
        loadingTeam.value = false;
      }
    }

    async function deleteTeam(teamId: string) {
      await api.delete(`/teams/${teamId}`);
      teams.value = teams.value.filter(team => team.id !== teamId);
      currentTeam.value = teams.value[0] ?? ({} as Team);
    }

    async function editTeamName({id, name}: {id: string; name: string}) {
      await api.patch(`/teams/${id}`, {name});
      await loadTeams();
      if (currentTeam.value?.id === id) {
        await loadTeamById(id);
      }
    }

    async function addMemberToTeam({
      role,
      email,
      team,
    }: {
      role: string;
      email: string;
      team: Team;
    }) {
      await api.post('/members', {
        developerId: email,
        teamId: team.id,
        role,
      });
      return loadTeamById(team.id);
    }

    async function transferTeamOwnership({
      newOwner,
      owner,
    }: {
      newOwner: string;
      owner: string;
    }) {
      await updateMemberRole({id: newOwner, role: 'Owner'});
      return updateMemberRole({id: owner, role: 'Admin'});
    }

    async function removeMember({memberToLeave}: {memberToLeave: string}) {
      const teamId =
        teams.value.find(team =>
          team.members?.some(member => member.id === memberToLeave),
        )?.id ?? currentTeam.value?.id;

      await api.delete(`/members/${memberToLeave}`);

      if (teamId) {
        applyTeamWithoutMember(teamId, memberToLeave);
        await loadTeamById(teamId);
        // A stale include payload must not resurrect a member we just deleted.
        applyTeamWithoutMember(teamId, memberToLeave);
        return getTeamById(teamId);
      }

      return loadTeams();
    }

    async function updateMemberRole(member: {id: string; role: string}) {
      const teamId =
        teams.value.find(team =>
          team.members?.some(teamMember => teamMember.id === member.id),
        )?.id ?? currentTeam.value?.id;

      await api.patch(`/members/${member.id}`, {role: member.role});

      if (teamId) {
        return loadTeamById(teamId);
      }

      return loadTeams();
    }

    async function shareApplication({
      appId,
      teamId,
      environmentId,
    }: {
      appId: string;
      teamId: string;
      environmentId: string;
    }) {
      await api.post(
        `/teams/${teamId}/sharedapplications`,
        {appId},
        {params: {environmentId}},
      );
      await loadTeams();
    }

    async function unshareApplication({id}: {id: string}) {
      const response = await api.delete(`/sharedapplications/${id}`);
      await loadTeams();
      return response;
    }

    async function unlinkPermissionGroupFromTeam({
      teamId,
      permissionGroupId,
    }: {
      teamId: string;
      permissionGroupId: string;
    }) {
      await api.delete(
        `/teams/${teamId}/permissiongroups/rel/${permissionGroupId}`,
      );
      return loadTeamById(teamId);
    }

    async function linkPermissionGroupToTeam({
      teamId,
      permissionGroupId,
    }: {
      teamId: string;
      permissionGroupId: string;
    }) {
      await api.put(
        `/teams/${teamId}/permissiongroups/rel/${permissionGroupId}`,
        {},
      );
      return loadTeamById(teamId);
    }

    function $reset() {
      teams.value = [];
      createdTeamId.value = null;
      hasTeam.value = null;
      newOptions.value = {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        groupBy: [],
        multiSort: false,
        mustSort: false,
        filter: {},
      };
      permissionGroupOptions.value = {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        groupBy: [],
        multiSort: false,
        mustSort: false,
        filter: {},
      };
      memberOptions.value = {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        groupBy: [],
        multiSort: false,
        mustSort: false,
        filter: {},
      };
      permissionGroupTOptions.value = {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        groupBy: [],
        multiSort: false,
        mustSort: false,
        filter: {},
      };
      apisOptions.value = {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        groupBy: [],
        multiSort: false,
        mustSort: false,
        filter: {},
      };
      newSearch.value = '';
      currentTeam.value = {} as Team;
    }

    return {
      teams,
      createdTeamId,
      hasTeam,
      newOptions,
      permissionGroupOptions,
      memberOptions,
      permissionGroupTOptions,
      apisOptions,
      newSearch,
      currentTeam,
      loadingTeam,
      getSharedTeams,
      getNotSharedTeams,
      getTeamId,
      getTeamById,
      getTeamMembersTable,
      createTeam,
      loadTeams,
      loadTeamById,
      deleteTeam,
      editTeamName,
      addMemberToTeam,
      transferTeamOwnership,
      removeMember,
      updateMemberRole,
      shareApplication,
      unshareApplication,
      unlinkPermissionGroupFromTeam,
      linkPermissionGroupToTeam,
      $reset,
    };
  },
  {
    persist: {
      pick: [
        'newOptions',
        'newSearch',
        'teams',
        'permissionGroupOptions',
        'memberOptions',
        'permissionGroupTOptions',
        'apisOptions',
      ],
    },
  },
);
